import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { ModalController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { TypeIntegration } from "src/model/enum/enumDigitalPlace";

/** One selectable integration shown as a card in the popup */
export interface IntegrationOption {
  kind: TypeIntegration;
  name: string;
  /** Translation key of the short description shown under the name */
  descriptionKey: string;
  /** Path of the logo image (e.g. "assets/img/integrations/ced-house.svg") */
  logo: string;
}

/**
 * Popup that lets the administrator pick which external management software
 * (CED House, iCond, ...) to configure for their condominiums.
 * Each integration is rendered as a card; the chosen one is highlighted.
 * Dismisses with `{ kind }` on confirm, or with no data when cancelled.
 */
@Component({
  selector: "app-popup-select-integration",
  templateUrl: "./popup-select-integration.component.html",
  styleUrls: ["./popup-select-integration.component.scss"],
  standalone: false,
})
export class PopupSelectIntegrationComponent implements OnInit, OnDestroy {
  /** Integrations to choose from */
  @Input() options: Array<IntegrationOption> = [];
  /** Integration pre-selected when the popup opens (optional) */
  @Input() selectedKind: TypeIntegration = null;

  public titleLabel: string = "";
  public messageLabel: string = "";
  public confirmLabel: string = "";
  public cancelLabel: string = "";

  private _translationSubscription: Subscription = null;

  constructor(
    private _modalController: ModalController,
    private _translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadTranslation();
    this._translationSubscription = this._translateService.onLangChange.subscribe(
      () => this.loadTranslation(),
    );
  }

  ngOnDestroy(): void {
    this._translationSubscription?.unsubscribe();
  }

  /** Highlight the clicked card */
  public onSelect(option: IntegrationOption): void {
    this.selectedKind = option.kind;
  }

  public isSelected(option: IntegrationOption): boolean {
    return this.selectedKind === option.kind;
  }

  public onConfirm(): void {
    if (this.selectedKind === null) {
      return;
    }

    this._modalController.dismiss({ kind: this.selectedKind });
  }

  public onCancel(): void {
    this._modalController.dismiss();
  }

  private loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.enableIntegration",
        "dictionary.selectIntegration",
        "dictionary.confirm",
        "dictionary.undo",
      ])
      .subscribe((translation) => {
        this.titleLabel = translation["dictionary.enableIntegration"];
        this.messageLabel = translation["dictionary.selectIntegration"];
        this.confirmLabel = translation["dictionary.confirm"];
        this.cancelLabel = translation["dictionary.undo"];
      });
  }
}
